// The 5 coarse labels a saved result carries (Stage 2, docs/property-strategy-spec.md S2.3): the strategy tag, the
// report title's own words for the admin list, the build plan's 4 families, the funding position and the experience;
// and the 1 next step those labels point to (nextStep), which the report's worksheet button and email 5 both read.
// The 1 place these rules live: the browser (src/strategy) and the server copy (supabase/functions/_shared/strategy,
// held to this file byte for byte by tests/strategy/server-copy.test.mjs) both import it. Pure, and it imports only
// ./engine.mjs. The rules are Adam's to confirm before Stage 3 (S2.13, point 9; he changed the funding rule on
// 27 Oct 2026, and on 28 Oct made the sourcing family own-money and set the next step, then said yes to the starter
// guides' recommendations: every reader the report reads as an investor is money only, and an undecided result its
// goal places in the sourcing family is own-money); the tests pin them.
//
// Never an answer, an amount, a risk detail or the report: each label is 1 of a short fixed list.
import {names} from './engine.mjs';

export const STRATEGIES=['brrr','buy-to-let','hmo','serviced-accommodation','rent-to-rent','lease-option','flip','commercial-conversion','development','deal-sourcing','assisted-sale','investing','undecided'];
export const FAMILIES=['rentals','projects','sourcing','investing'];
export const FUNDING_POSITIONS=['own-money','partner-or-investor'];
export const EXPERIENCE=['new','some-deals'];
export const NEXT_STEPS=['none','masterclass','scorecard','property-school'];

// The engine's strategy ids to their tags
const tagOf={BRR:'brrr',BTL:'buy-to-let',SA:'serviced-accommodation',HMO:'hmo',CC:'commercial-conversion',DEV:'development',DS:'deal-sourcing',FLIP:'flip'};
const familyOf={brrr:'rentals','buy-to-let':'rentals',hmo:'rentals','serviced-accommodation':'rentals','rent-to-rent':'rentals','lease-option':'rentals',flip:'projects','commercial-conversion':'projects',development:'projects','deal-sourcing':'sourcing','assisted-sale':'sourcing',investing:'investing'};
// For a result with no strategy yet, the financial goal (C03) decides the family
const goalFamily={A:'rentals',B:'projects',C:'sourcing',D:'rentals'};

const list=v=>Array.isArray(v)?v:v===undefined||v===null?[]:[v];
const has=(answers,id,value)=>list(answers?.[id]).includes(value);

/**
 * Money only: role D, whatever the status. The engine gives role D only to a visitor who wants to provide money
 * without running or overseeing the activity (scope_qualified, or preparation_first when their answers raise an
 * obstacle) or who chose money only and keeping the final say and hasn't settled which (roleContext.unresolved:
 * clarification, or preparation_first). That is the report's own capital || roleUnclear test (report-content.mjs
 * reportSubject), the readers shown "Write your investor brief" or "Decide what "in control" means to you". Adam,
 * 28 October 2026, on the starter guides' recommendations: "Yes, all of them." So they are labelled investors, with no
 * offer, and each investor guide, whose 1 version has no offer, is reached by every reader its exercise is shown to.
 */
export function moneyOnly(result){return result?.role==='D';}

/** The lead strategy, found as funding.mjs describeFunding finds it, or null. */
export function leadStrategy(result){
 const primary=list(result?.primary),chosen=primary.filter(x=>x!=='NMD');
 return (chosen.length>1&&result?.closeRanking?result.closeRanking.lead:chosen[0])||result?.selection?.lead||null;
}

export function strategyTag(result){
 if(moneyOnly(result))return 'investing';
 if(result?.access?.mode==='lease_option')return 'lease-option';
 if(result?.access?.mode==='rent_to_rent')return 'rent-to-rent';
 if(list(result?.primary).includes('NMD')&&result?.nmdMode==='defined_nonbuying_participation')return 'assisted-sale';
 return tagOf[leadStrategy(result)]||'undecided';
}

export function strategyFamily(strategy,answers){
 return familyOf[strategy]||goalFamily[answers?.C03]||'rentals';
}

/** The report title's own words (app.js builds the title from the same names), at most 120 characters. */
export function strategyName(result){
 const primary=list(result?.primary);
 let name='Not decided yet';
 if(moneyOnly(result))name='Investing money only';
 else if(primary.length)name=primary.map(id=>names[id]||id).join(' or ');
 else if(result?.selection?.lead&&names[result.selection.lead])name=names[result.selection.lead];
 return name.length>120?name.slice(0,120).trimEnd():name;
}

// Adam, 27 October 2026: being open to a partner for a share (PF04 A) no longer makes a lead partner-or-investor.
// Adam, 28 October 2026: "Somebody who ends up in deal sourcing doesn't need a partner." The sourcing family, as
// strategyFamily works it out, is always own-money, as money only is: its 2 strategies, deal-sourcing and
// assisted-sale, and, since his yes the same day to the starter guides' recommendations ("Yes, all of them."), an
// undecided result whose goal (C03 C) places it in the family. With every role D result money only, no fixture session
// or random run of the tool is undecided any more (worksheets.test.mjs), so that last part is kept for a result the
// engine might give later.
export function fundingPosition(result,answers){
 if(moneyOnly(result)||strategyFamily(strategyTag(result),answers)==='sourcing')return 'own-money';
 const noDeposit=['A','B'].includes(answers?.C07)&&!has(answers,'C09','D');
 return noDeposit||has(answers,'C09','B')||answers?.PF03==='C'?'partner-or-investor':'own-money';
}

export function experience(answers){
 return ['once','repeated'].includes(answers?.B04)||answers?.B10==='property'||has(answers,'B01','business')||has(answers,'C13','E')||has(answers,'C13','F')?'some-deals':'new';
}

// The next step's 3 strategies that go to the masterclass however they're funded (rule d below)
const masterclassStrategies=['lease-option','rent-to-rent','assisted-sale'];

/**
 * The 1 next step a result's labels point to, the same for the report's worksheet button and email 5 (Adam, 28 October
 * 2026: "They need to point to the same step."). It takes { strategy, strategy_family, funding_position, experience }
 * and the first rule that applies wins: (a) the investing family, none (no offer); (b) partner-or-investor, masterclass;
 * (c) some-deals, scorecard; (d) lease-option, rent-to-rent or assisted-sale, masterclass; (e) everyone else,
 * property-school. Purchases (already in Property School, or bought the masterclass) are MailerLite's, not this.
 */
export function nextStep(labels){
 if(labels?.strategy_family==='investing')return 'none';
 if(labels?.funding_position==='partner-or-investor')return 'masterclass';
 if(labels?.experience==='some-deals')return 'scorecard';
 if(masterclassStrategies.includes(labels?.strategy))return 'masterclass';
 return 'property-school';
}

/** The 5 labels of a result: { strategy, strategy_name, strategy_family, funding_position, experience }. */
export function leadLabels(result,answers){
 const strategy=strategyTag(result);
 return {strategy,strategy_name:strategyName(result),strategy_family:strategyFamily(strategy,answers),funding_position:fundingPosition(result,answers),experience:experience(answers)};
}
